import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import { useSportConfig } from '../context/SportConfig';
import '../styles/AdminPlayers.css';

function AdminPlayers() {
  const sportConfig = useSportConfig();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(null); // { player }
  const [editCost, setEditCost] = useState('');
  const [editRanking, setEditRanking] = useState('');
  const [editStatus, setEditStatus] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { type, message, errors? }
  const [uploading, setUploading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/players`);
      setPlayers(response.data.players);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch players');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset file input so same file can be re-selected
    e.target.value = '';

    if (!file.name.endsWith('.csv')) {
      setUploadStatus({ type: 'error', message: 'Please select a CSV file' });
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setUploadStatus({ type: 'error', message: 'File exceeds 1MB limit' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setUploadStatus(null);
      const response = await axios.post(`${API_URL}/admin/players/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadStatus({ type: 'success', message: response.data.message });
      fetchPlayers(); // Refresh the grid
    } catch (err) {
      const data = err.response?.data;
      setUploadStatus({
        type: 'error',
        message: data?.message || 'Upload failed',
        errors: data?.errors || null,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const updates = {};
    const costVal = Number(editCost);
    const rankVal = Number(editRanking);

    if (editCost !== '' && (isNaN(costVal) || costVal < 0)) {
      setEditStatus({ type: 'error', message: 'Cost must be a non-negative number' });
      return;
    }
    if (editRanking !== '' && (isNaN(rankVal) || rankVal < 1)) {
      setEditStatus({ type: 'error', message: 'Ranking must be a positive number' });
      return;
    }

    if (editCost !== '') updates.costCredits = costVal;
    if (editRanking !== '') updates.ranking = rankVal;

    if (Object.keys(updates).length === 0) {
      setEditStatus({ type: 'error', message: 'No changes to save' });
      return;
    }

    try {
      await axios.put(`${API_URL}/admin/players/${editModal.player._id}`, updates);
      setEditStatus({ type: 'success', message: 'Player updated!' });
      fetchPlayers();
      setTimeout(() => {
        setEditModal(null);
        setEditStatus(null);
      }, 1500);
    } catch (err) {
      setEditStatus({ type: 'error', message: err.response?.data?.message || 'Update failed' });
    }
  };

  const EditButton = useCallback((params) => (
    <button
      className="btn-edit-player"
      onClick={() => {
        setEditModal({ player: params.data });
        setEditCost(String(params.data.costCredits));
        setEditRanking(String(params.data.ranking));
        setEditStatus(null);
      }}
    >
      Edit
    </button>
  ), []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      filter: true,
      minWidth: 180,
      sort: 'asc',
      sortIndex: 2,
      cellRenderer: (params) => (
        <a
          href={`https://google.com/search?q=${encodeURIComponent(params.value)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="player-link"
        >
          {params.value}
        </a>
      ),
    },
    {
      headerName: 'Tour',
      field: 'tour',
      sortable: true,
      filter: true,
      maxWidth: 90,
    },
    {
      headerName: 'Ranking',
      field: 'ranking',
      sortable: true,
      filter: 'agNumberColumnFilter',
      maxWidth: 110,
      sort: 'asc',
      sortIndex: 1,
    },
    {
      headerName: 'Cost (Credits)',
      field: 'costCredits',
      sortable: true,
      filter: 'agNumberColumnFilter',
      maxWidth: 140,
      sort: 'desc',
      sortIndex: 0,
    },
    {
      headerName: 'Actions',
      cellRenderer: EditButton,
      sortable: false,
      filter: false,
      maxWidth: 100,
    },
  ], [EditButton]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
  }), []);

  if (loading) return <div className="admin-loading">Loading players...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div className="admin-players-section">
      <div className="admin-players-header">
        <h3>{sportConfig?.appEmoji || '🎾'} Site Admin — All Players ({players.length})</h3>
      </div>

      <div className="csv-upload-section">
        <div className="csv-upload-row">
          <label className="btn btn-upload" htmlFor="csv-upload">
            {uploading ? 'Uploading...' : '📁 Upload CSV'}
          </label>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <span className="csv-upload-hint">
            Format: name, tour, ranking, costCredits — up to 1000 rows, 1MB max
          </span>
        </div>
        {uploadStatus && (
          <div className={`csv-upload-status ${uploadStatus.type}`}>
            <p>{uploadStatus.message}</p>
            {uploadStatus.errors && (
              <ul className="csv-error-list">
                {uploadStatus.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.errors.join('; ')}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="ag-theme-alpine admin-grid">
        <AgGridReact
          rowData={players}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={20}
          domLayout="autoHeight"
          animateRows={true}
        />
      </div>

      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Edit Player: {editModal.player.name}</h4>
            <p className="modal-subtitle">{editModal.player.tour}</p>
            <label className="modal-label">
              Ranking
              <input
                type="number"
                min="1"
                value={editRanking}
                onChange={(e) => setEditRanking(e.target.value)}
                className="modal-input"
              />
            </label>
            <label className="modal-label">
              Cost (Credits)
              <input
                type="number"
                min="0"
                value={editCost}
                onChange={(e) => setEditCost(e.target.value)}
                className="modal-input"
              />
            </label>
            {editStatus && (
              <p className={`modal-status ${editStatus.type}`}>{editStatus.message}</p>
            )}
            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={handleSave}>
                Save Changes
              </button>
              <button className="btn btn-cancel" onClick={() => setEditModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPlayers;
